import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ComponentType } from "../../../../types/component";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import {
  OFF_WING_TYPE_OPTIONS,
  OffWingTypeOption,
} from "../../../../utils/offWingType";
import type { AttachmentListFilter } from "../hooks/useComponents";
import type { Operator } from "../../../../types/operator";

const checkboxSx = {
  color: "#888",
  "&.Mui-checked": { color: "hsl(19 101% 51% / 1)" },
};

type Props = {
  filter: ComponentType | "All";
  onChange: (v: ComponentType | "All") => void;
  attachmentFilter: AttachmentListFilter;
  onAttachmentFilterChange: (v: AttachmentListFilter) => void;
  offWingTypeFilters: Set<OffWingTypeOption>;
  onOffWingTypeFilterToggle: (type: OffWingTypeOption) => void;
  operators: Operator[];
  operatorFilters: Set<string>;
  onOperatorFilterToggle: (id: string) => void;
  showOperatorFilter: boolean;
  onCreate: () => void;
};

export function ComponentFilterPane({
  filter,
  onChange,
  attachmentFilter,
  onAttachmentFilterChange,
  offWingTypeFilters,
  onOffWingTypeFilterToggle,
  operators,
  operatorFilters,
  onOperatorFilterToggle,
  showOperatorFilter,
  onCreate,
}: Readonly<Props>) {
  const types: (ComponentType | "All")[] = ["All", "Engine", "APU", "LandingGear"];
  const canCreateComponent = useHasPermission('assets-assemblies', 'create');

  return (
    <Paper
      sx={{
        width: 250,
        minWidth: 250,
        backgroundColor: "#111",
        border: "1px solid #333",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        alignSelf: "stretch",
        maxHeight: "100%",
        overflow: "hidden",
      }}
    >
      {/* ── Scrollable filter content ── */}
      <Box sx={{ overflowY: "auto", flexGrow: 1, p: 2 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, color: "hsl(19 101% 51% / 1)" }}
        >
          Filter Assemblies
        </Typography>

        <List disablePadding>
          {types.map((t) => (
            <ListItem key={t} disablePadding>
              <ListItemButton
                selected={filter === t}
                onClick={() => onChange(t)}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "hsl(19 101% 51% / 0.2)",
                    "&:hover": {
                      backgroundColor: "hsl(19 101% 51% / 0.3)",
                    },
                  },
                }}
              >
                <ListItemText
                  primary={t}
                  sx={{
                    "& .MuiListItemText-primary": {
                      color: filter === t ? "hsl(19 101% 51% / 1)" : "#fff",
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2, bgcolor: "#333" }} />

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={attachmentFilter === "attached"}
              onChange={() => onAttachmentFilterChange("attached")}
              sx={checkboxSx}
            />
          }
          label="Attached Components"
          sx={{
            m: 0,
            mb: 0.5,
            alignItems: "center",
            "& .MuiFormControlLabel-label": { color: "#fff", fontSize: "0.875rem" },
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={attachmentFilter === "nonAttached"}
              onChange={() => onAttachmentFilterChange("nonAttached")}
              sx={checkboxSx}
            />
          }
          label="Offwing Components"
          sx={{
            m: 0,
            mb: 0.5,
            alignItems: "center",
            "& .MuiFormControlLabel-label": { color: "#fff", fontSize: "0.875rem" },
          }}
        />
        {OFF_WING_TYPE_OPTIONS.map((offWingType, index) => (
          <FormControlLabel
            key={offWingType}
            control={
              <Checkbox
                size="small"
                checked={offWingTypeFilters.has(offWingType)}
                onChange={() => onOffWingTypeFilterToggle(offWingType)}
                sx={checkboxSx}
              />
            }
            label={offWingType}
            sx={{
              m: 0,
              ml: "25%",
              mb: index === OFF_WING_TYPE_OPTIONS.length - 1 ? 1.5 : 0.5,
              alignItems: "center",
              "& .MuiFormControlLabel-label": {
                color: "#fff",
                fontSize: "0.875rem",
              },
            }}
          />
        ))}

        {showOperatorFilter && operators.length > 0 && (
          <>
            <Divider sx={{ my: 2, bgcolor: "#333" }} />
            <Typography
              variant="subtitle2"
              sx={{
                color: "#aaa",
                mb: 1,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Operator
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
              {operators.map((op, index) => (
                <FormControlLabel
                  key={op.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={operatorFilters.has(op.id)}
                      onChange={() => onOperatorFilterToggle(op.id)}
                      sx={checkboxSx}
                    />
                  }
                  label={op.name}
                  sx={{
                    m: 0,
                    mb: index === operators.length - 1 ? 0 : 0.5,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      color: "#fff",
                      fontSize: "0.875rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              ))}
            </Box>
          </>
        )}
      </Box>

      {/* ── Add Assembly — always visible at the bottom ── */}
      {canCreateComponent && (
        <Box sx={{ p: 2, pt: 1, flexShrink: 0 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreate}
            sx={{
              backgroundColor: "hsl(19 101% 51% / 1)",
              "&:hover": {
                backgroundColor: "hsl(19 101% 41% / 1)",
              },
            }}
          >
            Add Assembly
          </Button>
        </Box>
      )}
    </Paper>
  );
}
